import React, { useEffect, useState } from 'react';

import { joinClassNames } from '../../util/util';
import Player from '../Player/Player';
import { getStoryClipInfo, getStoryThumbnailUrl, StoryFeedVideoItem } from './story-feed-helper';
import StoryClipInfo from './StoryClipInfo';

// Under this, the finger did not travel: a tap, not the start of a scroll.
const TAP_SLOP_PX = 10;

interface StoryVideoSlideProps {
	item: StoryFeedVideoItem;
	/** The slide the viewer is on. Only this one plays; the rest are mounted and paused. */
	isActive: boolean;
	/** False for slides outside the mounting window - they stay a poster and cost nothing. */
	isMounted: boolean;
	/** True once this clip is painting frames, so its still frame can come down. */
	hasPicture: boolean;
	settings: any;
	configParams: any;
	container?: any;
	layout: 'mobile' | 'desktop';
	token?: string;
	maintainProportions?: boolean;
	onPlayComplete: () => void;
	onPicture: (mediaId: string) => void;
	onTogglePlayback: () => void;
	handlePlayerHover?: any;
}

/**
 * One clip in the feed. A component rather than inline JSX because a slide is the only place
 * per-slide state can live - an interact slide in v2 needs its own scenario and scene, which are
 * single global values today. The seam: the feed hands a slide an item and isActive, and the slide
 * decides what to mount.
 */
const StoryVideoSlide = ({
	item,
	isActive,
	isMounted,
	hasPicture,
	settings,
	configParams,
	container,
	layout,
	token,
	maintainProportions,
	onPlayComplete,
	onPicture,
	onTogglePlayback,
	handlePlayerHover,
}: StoryVideoSlideProps) => {
	// A finger never produces a click here: GoBrain's video consumes the tap, so nothing is
	// synthesised. React's delegated pointer handlers do not reach this subtree either, and by the
	// time a stationary touchend has passed the slide it has been stopped short of the feed. What
	// does work is a native listener on the stage itself - the last place the event is still going.
	const [stageEl, setStageEl] = useState<HTMLDivElement | null>(null);
	useEffect(() => {
		if (!stageEl || !isActive) {
			return undefined;
		}
		let origin: { x: number; y: number } | null = null;
		const onStart = (event: TouchEvent) => {
			origin = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
		};
		const onEnd = (event: TouchEvent) => {
			const from = origin;
			const touch = event.changedTouches[0];
			origin = null;
			if (!from || !touch) {
				return;
			}
			if (Math.abs(touch.clientX - from.x) > TAP_SLOP_PX || Math.abs(touch.clientY - from.y) > TAP_SLOP_PX) {
				return;
			}
			// The text is a control, not part of the picture: expanding it must not also pause.
			// StoryClipInfo stops the React click, but this is a native listener on an ancestor, so
			// that never reaches it - the touch has to be excluded here, by where it landed.
			const target = event.target as HTMLElement | null;
			if (target?.closest?.('.qplayer-story-info')) {
				return;
			}
			onTogglePlayback?.();
		};
		stageEl.addEventListener('touchstart', onStart, { passive: true });
		stageEl.addEventListener('touchend', onEnd, { passive: true });
		return () => {
			stageEl.removeEventListener('touchstart', onStart);
			stageEl.removeEventListener('touchend', onEnd);
		};
	}, [stageEl, isActive, onTogglePlayback]);

	const media = item.media;
	const mediaId = media?.id;
	const thumbnail = getStoryThumbnailUrl(media);
	const { description } = getStoryClipInfo(media);

	return (
		<div className="qplayer-story-softbox__slide">
			{/* Clicking the video is how a reel is paused and resumed; the control bar above it
			    keeps its own buttons. A finger never gets here - GoBrain's video swallows the tap
			    so no click is synthesised - so the feed carries a touch tap of its own. */}
			<div
				ref={setStageEl}
				className="qplayer-story-softbox__stage"
				onClick={isActive ? onTogglePlayback : undefined}
				// The stylesheet decides what becomes of the frame - blurred behind, fitted in front.
				style={{ '--qplayer-story-thumb': `url(${thumbnail})` } as React.CSSProperties}
			>
				{/* The frame is its own layer so neither the card teased at the edge of the screen
				    nor one still opening is ever a black box. It stays in front until this clip
				    really has picture, which is what makes the swipe look instant. */}
				<div
					className={joinClassNames(
						'qplayer-story-softbox__poster',
						isActive && hasPicture ? '' : 'qplayer-story-softbox__poster--front'
					)}
				/>
				{isMounted ? (
					<Player
						accountId={configParams?.accountId as string}
						isVisible={isActive}
						playerMedia={mediaId}
						settings={settings}
						configParams={configParams}
						layout={layout}
						isMaintainProportions={maintainProportions}
						playerId={`player-${mediaId}-${configParams?.containerKey}`}
						token={token}
						handlePlayerHover={handlePlayerHover}
						container={container}
						mediaData={media}
						onPlayComplete={onPlayComplete}
						onPicture={() => onPicture(mediaId)}
					/>
				) : null}
				<StoryClipInfo description={description} isActive={isActive} />
			</div>
		</div>
	);
};

export default StoryVideoSlide;
